'use client';
import { useTranslations } from 'next-intl';
import { useFeedback } from 'providers/feedback-provider';
import { useQuickToast } from 'hooks/use-toast';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import { SelectedEquipmentInfo } from 'components/my-equipment/selected-equipment/selected-equipment-info';
import { ErrorBannerMessage } from 'components/my-equipment/error-banner-message';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { BUTTON_VARIANTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import {
  MyEquipmentLinkContent,
  MyEquipmentFooter,
  MyEquipmentButton,
  Conditional
} from '@cat-ecom/pcc-components';
import {
  GENERIC_ERROR,
  WIDGET_IDENTIFIER
} from 'components/my-equipment/constants';
import { SelectedEquipmentInfo as SelectedEquipmentInfoType } from 'app/[lang]/[esite]/(core)/_header/components/equipment-widget/types';
import { useCoreContext } from 'providers/core-provider';

export const DeleteEquipmentDrawerForm = ({
  selectedEquipmentInfo
}: {
  selectedEquipmentInfo: SelectedEquipmentInfoType;
}) => {
  const toast = useQuickToast();
  const t = useTranslations();
  const { deleteEquipment } = useCoreContext();
  const UNKNOWN_ERROR_MESSAGE = t('UNKNOWN_ERROR_MESSAGE');
  const { updateFeedback, showFeedback, dismissFeedback } = useFeedback();
  const { deleteEquipmentDrawer, closeAllDrawers } = useMyEquipmentDrawer();
  const serialNumber = selectedEquipmentInfo.serialNumber || '';
  const selectedEquipmentInfoModified = {
    productId: selectedEquipmentInfo.assetId || '',
    serialNumber,
    imageURL: selectedEquipmentInfo.imageURL,
    equipmentFamily: selectedEquipmentInfo.productFamily,
    model: selectedEquipmentInfo.model,
    name: selectedEquipmentInfo?.name
  };
  const toastMessage = t('MEQ_DELETE_MESSAGE', {
    0: selectedEquipmentInfo.model,
    1: capitalizeText(selectedEquipmentInfo.productFamily)
  });

  const handleOnClickCancel = () => {
    dismissFeedback();
    deleteEquipmentDrawer.closeDrawer();
  };

  const handleDeleteEquipment = async () => {
    const { status, data } = await deleteEquipment(
      selectedEquipmentInfo.unvalidatedItemListId,
      serialNumber
    );

    if (status === 'success') {
      closeAllDrawers();
      toast.success({
        message: toastMessage,
        delay: 5
      });
    }

    if (status === 'error' && data?.type === GENERIC_ERROR) {
      updateFeedback(UNKNOWN_ERROR_MESSAGE);
    }
  };

  return (
    <>
      <Conditional test={showFeedback}>
        <ErrorBannerMessage
          buttonLabel={t('CAT_CONTACT_US_TITLE')}
          bannerLabel={UNKNOWN_ERROR_MESSAGE}
        />
      </Conditional>
      <MyEquipmentLinkContent classname="mb-3 pb-2 mt-3">
        <CatHeading variant="label-lg">{t('DELETE_WIDGET_HEADER')}</CatHeading>
      </MyEquipmentLinkContent>
      <SelectedEquipmentInfo
        currentEquipment={selectedEquipmentInfoModified}
        identifier={WIDGET_IDENTIFIER.EDIT}
      />
      <MyEquipmentFooter>
        <MyEquipmentButton variant="danger" onClick={handleDeleteEquipment}>
          {t('DELETE')}
        </MyEquipmentButton>
        <MyEquipmentButton
          onClick={handleOnClickCancel}
          variant={BUTTON_VARIANTS.GHOST}
          className="pl-3"
        >
          {t('CANCEL')}
        </MyEquipmentButton>
      </MyEquipmentFooter>
    </>
  );
};
